import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type SheetTreeDb = {
  bookId: string;
  sheetId: string;
  order: number;
  parentSheetId?: string;
  goodsId?: string;
  version?: number;
}
const initial: SheetTreeDb = {
  bookId: '',
  sheetId: '',
  order: 0,
  parentSheetId: undefined,
  goodsId: undefined,
  version: undefined
};

export type SheetTreeDbHashType = {
  [key in string]: SheetTreeDb;
}

export type SheetTreeAddPayload = {
  id?: string;
} & SheetTreeDb;
export type SheetTreeUpdatePayload = {
  id: string;
} & { [P in keyof SheetTreeDb]?: SheetTreeDb[P] };

export type SheetTreeDbState = {
  sheetTrees: SheetTreeDbHashType;
}

export const generateSheetTreeId = (): string => generateId('st');
let initialState: SheetTreeDbState = { sheetTrees: {} };
const key = 'sheetTrees';

const sheetTreeDbSlice = createSlice({
  name: 'sheetTreeDb',
  initialState,
  reducers: {
    initializeSheetTrees(state: SheetTreeDbState) {
      state.sheetTrees = {};
    },
    addSheetTree(state: SheetTreeDbState, action: PayloadAction<SheetTreeAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateSheetTreeId();
      state.sheetTrees[id] = obj;
      saveData(key, id, obj);
    },
    updateSheetTree(state: SheetTreeDbState, action: PayloadAction<SheetTreeUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.sheetTrees[id] === undefined) {
        console.log(`*** SheetTree not found: ${id}`);
      } else {
        const newData = { ...state.sheetTrees[id], ...obj };
        state.sheetTrees[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteSheetTree(state: SheetTreeDbState, action: PayloadAction<SheetTreeUpdatePayload>) {
      const id = action.payload.id;
      delete state.sheetTrees[id];
      deleteData(key, id);
    },
    saveSheetTrees(state: SheetTreeDbState) {
      saveAllData(key, state.sheetTrees);
    },
    signUpSheetTrees(state: SheetTreeDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.sheetTrees, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: SheetTreeDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.sheetTrees = updatedValues;
      if (changed) saveAllData(key, state.sheetTrees);
    })
  }
})

export const { initializeSheetTrees, addSheetTree, updateSheetTree, deleteSheetTree, saveSheetTrees, signUpSheetTrees } = sheetTreeDbSlice.actions;
export default sheetTreeDbSlice.reducer;
